import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from "react";
import {useSearchParams} from "react-router-dom";
import type {SourceInfo} from "../../../types";

/** The search param name that keeps the open island in the chat url. */
export const ISLAND_SEARCH_PARAM = "island";

export type WorkIslandMode = "content-wide" | "balanced" | "chat-wide";

export type WorkIslandRequest = {
    kind: "topic";
    source: SourceInfo;
    initialMode?: WorkIslandMode;
};

const CONTENT_FRACTIONS: Record<WorkIslandMode, number> = {
    "chat-wide": 0.3,
    balanced: 0.6,
    "content-wide": 0.7,
};

const modeFromFraction = (fraction: number): WorkIslandMode => {
    if (fraction < 0.4) return "chat-wide";
    if (fraction > 0.6) return "content-wide";
    return "balanced";
};

export type WorkIslandContextValue = {
    request: WorkIslandRequest | null;
    mode: WorkIslandMode;
    contentFraction: number;
    openIsland: (request: WorkIslandRequest) => void;
    closeIsland: () => void;
    setMode: (mode: WorkIslandMode) => void;
    setContentFraction: (fraction: number) => void;
};

const defaultContext: WorkIslandContextValue = {
    request: null,
    mode: "balanced",
    contentFraction: CONTENT_FRACTIONS.balanced,
    openIsland: () => {},
    closeIsland: () => {},
    setMode: () => {},
    setContentFraction: () => {},
};

const WorkIslandContext = createContext<WorkIslandContextValue>(defaultContext);

export function WorkIslandProvider({children}: {children: ReactNode}) {
    const [request, setRequest] = useState<WorkIslandRequest | null>(null);
    const [contentFraction, setContentFractionState] = useState(
        CONTENT_FRACTIONS.balanced
    );
    const [searchParams, setSearchParams] = useSearchParams();

    const setContentFraction = useCallback((fraction: number) => {
        setContentFractionState(Math.min(0.7, Math.max(0.3, fraction)));
    }, []);

    const setMode = useCallback((mode: WorkIslandMode) => {
        setContentFractionState(CONTENT_FRACTIONS[mode]);
    }, []);

    // Write the open island into the url so a browser Back restores it.
    // Skip the write when the url already carries this exact island, for
    // example when a mount-time restore re-opens the island from the url.
    const openIsland = useCallback(
        (nextRequest: WorkIslandRequest) => {
            setRequest(nextRequest);
            setContentFractionState(
                CONTENT_FRACTIONS[nextRequest.initialMode ?? "balanced"]
            );
            const encodedUrl = nextRequest.source.url;
            if (searchParams.get(ISLAND_SEARCH_PARAM) !== encodedUrl) {
                setSearchParams(
                    (prev) => {
                        const next = new URLSearchParams(prev);
                        next.set(ISLAND_SEARCH_PARAM, encodedUrl);
                        return next;
                    },
                    {replace: false}
                );
            }
        },
        [searchParams, setSearchParams]
    );

    const closeIsland = useCallback(() => {
        setRequest(null);
        // Always use the latest params, because a pending open write may not be applied yet.
        setSearchParams(
            (prev) => {
                if (!prev.has(ISLAND_SEARCH_PARAM)) return prev;
                const next = new URLSearchParams(prev);
                next.delete(ISLAND_SEARCH_PARAM);
                return next;
            },
            {replace: true}
        );
    }, [setSearchParams]);

    const value = useMemo(
        () => ({
            request,
            mode: modeFromFraction(contentFraction),
            contentFraction,
            openIsland,
            closeIsland,
            setMode,
            setContentFraction,
        }),
        [
            request,
            contentFraction,
            openIsland,
            closeIsland,
            setMode,
            setContentFraction,
        ]
    );

    return (
        <WorkIslandContext.Provider value={value}>
            {children}
        </WorkIslandContext.Provider>
    );
}

export function useWorkIsland(): WorkIslandContextValue {
    return useContext(WorkIslandContext);
}
